/**
 * Validation functions for reading list rules
 */

import { isInYearRange } from './centuryUtils';

// Rule type constants
export const RULE_TYPES = {
    BOOKS_PER_AUTHOR: 'BOOKS_PER_AUTHOR',
    BOOKS_BY_TYPE: 'BOOKS_BY_TYPE',
    BOOKS_BY_COUNTRY: 'BOOKS_BY_COUNTRY',
    BOOKS_BY_YEAR_RANGE: 'BOOKS_BY_YEAR_RANGE',
    TOTAL_COUNT: 'TOTAL_COUNT',
    COMBINED: 'COMBINED'
};

// Operator constants
export const OPERATORS = {
    MAX: 'MAX',
    MIN: 'MIN'
};

// Literature type constants
export const LITERATURE_TYPES = {
    CZECH: 'CZECH',
    WORLD: 'WORLD'
};

/**
 * Generates a unique ID for a rule
 * @returns {string} Unique rule ID
 */
export const generateRuleId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    // Fallback for older browsers
    return `rule-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Generates a unique ID for a rule group
 * @returns {string} Unique group ID
 */
export const generateGroupId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return `group-${crypto.randomUUID()}`;
    }
    // Fallback for older browsers
    return `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Validates BOOKS_PER_AUTHOR rule
 * @param {Object} rule - Rule object with operator and value properties
 * @param {Array} books - Array of book objects
 * @returns {{isSatisfied: boolean, current: number, required: number, message: string, details: Object}}
 */
const validateBooksPerAuthor = (rule, books) => {
    const authorCounts = {};

    // Count books per author
    books.forEach(book => {
        if (book.author && book.author.id) {
            const authorId = book.author.id;
            authorCounts[authorId] = (authorCounts[authorId] || 0) + 1;
        }
    });

    if (rule.operator === OPERATORS.MAX) {
        // Check if any author exceeds the limit
        let maxCount = 0;
        let violatingAuthor = null;

        Object.entries(authorCounts).forEach(([authorId, count]) => {
            if (count > maxCount) {
                maxCount = count;
                const book = books.find(b => b.author && b.author.id === authorId);
                violatingAuthor = book ? book.author.name : 'Neznámý autor';
            }
        });

        const isSatisfied = maxCount <= rule.value;

        return {
            isSatisfied,
            current: maxCount,
            required: rule.value,
            message: isSatisfied
                ? `Splněno: Max ${rule.value} ${rule.value === 1 ? 'kniha' : 'knihy'} od autora`
                : `Nesplněno: ${violatingAuthor} má ${maxCount} knih (max ${rule.value})`,
            details: {
                authorCounts,
                maxCount,
                violatingAuthor: isSatisfied ? null : violatingAuthor
            }
        };
    } else if (rule.operator === OPERATORS.MIN) {
        // Check if there's at least one author with minimum books
        let maxCount = 0;
        let topAuthor = null;

        Object.entries(authorCounts).forEach(([authorId, count]) => {
            if (count > maxCount) {
                maxCount = count;
                const book = books.find(b => b.author && b.author.id === authorId);
                topAuthor = book ? book.author.name : 'Neznámý autor';
            }
        });

        const isSatisfied = maxCount >= rule.value;

        return {
            isSatisfied,
            current: maxCount,
            required: rule.value,
            message: isSatisfied
                ? `Splněno: Min ${rule.value} ${rule.value === 1 ? 'kniha' : 'knih'} od autora (${topAuthor}: ${maxCount})`
                : `Nesplněno: Nejvíce knih od autora: ${maxCount} (min ${rule.value})`,
            details: {
                authorCounts,
                maxCount,
                topAuthor
            }
        };
    }
};

/**
 * Validates BOOKS_BY_TYPE rule
 * @param {Object} rule - Rule object with bookType, operator and value properties
 * @param {Array} books - Array of book objects
 * @returns {{isSatisfied: boolean, current: number, required: number, message: string, details: Object}}
 */
const validateBooksByType = (rule, books) => {
    const count = books.filter(book => book.bookType === rule.bookType).length;

    // Get readable type label
    const typeLabels = {
        PROSE: 'prózy',
        POETRY: 'poezie',
        DRAMA: 'dramat'
    };
    const typeLabel = typeLabels[rule.bookType] || rule.bookType;

    let isSatisfied;
    let message;

    if (rule.operator === OPERATORS.MAX) {
        isSatisfied = count <= rule.value;
        message = isSatisfied
            ? `Splněno: Max ${rule.value} ${typeLabel} (${count}/${rule.value})`
            : `Nesplněno: Max ${rule.value} ${typeLabel} (${count}/${rule.value})`;
    } else if (rule.operator === OPERATORS.MIN) {
        isSatisfied = count >= rule.value;
        message = isSatisfied
            ? `Splněno: Min ${rule.value} ${typeLabel} (${count}/${rule.value})`
            : `Nesplněno: Min ${rule.value} ${typeLabel} (${count}/${rule.value})`;
    }

    return {
        isSatisfied,
        current: count,
        required: rule.value,
        message,
        details: {
            bookType: rule.bookType,
            count
        }
    };
};

/**
 * Helper function to check if a country is Czech
 * Checks if the country string contains "česk" (case insensitive)
 * @param {string} country - Country name
 * @returns {boolean}
 */
const isCzechCountry = (country) => {
    if (!country) return false;
    return country.toLowerCase().includes('česk');
};

/**
 * Validates BOOKS_BY_COUNTRY rule
 * @param {Object} rule - Rule object with literatureType, operator and value properties
 * @param {Array} books - Array of book objects
 * @returns {{isSatisfied: boolean, current: number, required: number, message: string, details: Object}}
 */
const validateBooksByCountry = (rule, books) => {
    // Filter based on literature type (Czech or World)
    let count;
    let literatureLabel;

    if (rule.literatureType === LITERATURE_TYPES.CZECH) {
        count = books.filter(book => isCzechCountry(book.countryOfOrigin)).length;
        literatureLabel = 'české literatury';
    } else if (rule.literatureType === LITERATURE_TYPES.WORLD) {
        count = books.filter(book => book.countryOfOrigin && !isCzechCountry(book.countryOfOrigin)).length;
        literatureLabel = 'světové literatury';
    } else {
        // Fallback for old rules with country field
        count = books.filter(book => book.countryOfOrigin === rule.country).length;
        literatureLabel = `knih z ${rule.country}`;
    }

    let isSatisfied;
    let message;

    if (rule.operator === OPERATORS.MAX) {
        isSatisfied = count <= rule.value;
        message = isSatisfied
            ? `Splněno: Max ${rule.value} knih ${literatureLabel}`
            : `Nesplněno: Max ${rule.value} knih ${literatureLabel}`;
    } else if (rule.operator === OPERATORS.MIN) {
        isSatisfied = count >= rule.value;
        message = isSatisfied
            ? `Splněno: Min ${rule.value} knih ${literatureLabel}`
            : `Nesplněno: Min ${rule.value} knih ${literatureLabel}`;
    }

    return {
        isSatisfied,
        current: count,
        required: rule.value,
        message,
        details: {
            literatureType: rule.literatureType,
            country: rule.country, // Keep for backwards compatibility
            count
        }
    };
};

/**
 * Validates BOOKS_BY_YEAR_RANGE rule
 * @param {Object} rule - Rule object with yearFrom, yearTo, operator and value properties
 * @param {Array} books - Array of book objects
 * @returns {{isSatisfied: boolean, current: number, required: number, message: string, details: Object}}
 */
const validateBooksByYearRange = (rule, books) => {
    const count = books.filter(book =>
        isInYearRange(book.publicationYear, rule.yearFrom, rule.yearTo)
    ).length;

    let isSatisfied;
    let message;

    // Format range description
    let rangeDesc;
    if (rule.yearFrom && rule.yearTo) {
        rangeDesc = `z let ${rule.yearFrom}-${rule.yearTo}`;
    } else if (rule.yearFrom) {
        rangeDesc = `od roku ${rule.yearFrom}`;
    } else if (rule.yearTo) {
        rangeDesc = `do roku ${rule.yearTo}`;
    } else {
        rangeDesc = `z jakéhokoliv roku`;
    }

    if (rule.operator === OPERATORS.MAX) {
        isSatisfied = count <= rule.value;
        message = isSatisfied
            ? `Splněno: Max ${rule.value} knih ${rangeDesc} (${count}/${rule.value})`
            : `Nesplněno: Max ${rule.value} knih ${rangeDesc} (${count}/${rule.value})`;
    } else if (rule.operator === OPERATORS.MIN) {
        isSatisfied = count >= rule.value;
        message = isSatisfied
            ? `Splněno: Min ${rule.value} knih ${rangeDesc} (${count}/${rule.value})`
            : `Nesplněno: Min ${rule.value} knih ${rangeDesc} (${count}/${rule.value})`;
    }

    return {
        isSatisfied,
        current: count,
        required: rule.value,
        message,
        details: {
            yearFrom: rule.yearFrom,
            yearTo: rule.yearTo,
            count
        }
    };
};

/**
 * Validates TOTAL_COUNT rule
 * @param {Object} rule - Rule object with operator and value properties
 * @param {Array} books - Array of book objects
 * @returns {{isSatisfied: boolean, current: number, required: number, message: string, details: Object}}
 */
const validateTotalCount = (rule, books) => {
    const count = books.length;
    let isSatisfied, message;

    if (rule.operator === OPERATORS.MAX) {
        isSatisfied = count <= rule.value;
        message = isSatisfied
            ? `Splněno: Celkem ${count} ${count === 1 ? 'kniha' : 'knih'} (max ${rule.value})`
            : `Nesplněno: Celkem ${count} knih (max ${rule.value})`;
    } else if (rule.operator === OPERATORS.MIN) {
        isSatisfied = count >= rule.value;
        message = isSatisfied
            ? `Splněno: Celkem ${count} ${count === 1 ? 'kniha' : 'knih'} (min ${rule.value})`
            : `Nesplněno: Celkem ${count} knih (min ${rule.value})`;
    }

    return {
        isSatisfied,
        current: count,
        required: rule.value,
        message,
        details: { count }
    };
};

/**
 * Generates a label for a rule
 * @param {Object} rule - Rule object
 * @returns {string} Label for the rule
 */
export const generateRuleLabel = (rule) => {
    const operatorLabel = rule.operator === OPERATORS.MAX ? 'Max' : 'Min';

    switch (rule.type) {
        case RULE_TYPES.BOOKS_PER_AUTHOR:
            return `${operatorLabel} ${rule.value} ${rule.value === 1 ? 'kniha' : 'knihy'} od autora`;

        case RULE_TYPES.BOOKS_BY_TYPE:
            const typeLabels = {
                PROSE: 'prózy',
                POETRY: 'poezie',
                DRAMA: 'dramat'
            };
            const typeLabel = typeLabels[rule.bookType] || rule.bookType;
            return `${operatorLabel} ${rule.value} ${typeLabel}`;

        case RULE_TYPES.BOOKS_BY_COUNTRY:
            if (rule.literatureType === LITERATURE_TYPES.CZECH) {
                return `${operatorLabel} ${rule.value} ${rule.value === 1 ? 'kniha' : 'knih'} české literatury`;
            } else if (rule.literatureType === LITERATURE_TYPES.WORLD) {
                return `${operatorLabel} ${rule.value} ${rule.value === 1 ? 'kniha' : 'knih'} světové literatury`;
            } else {
                // Fallback for old rules
                return `${operatorLabel} ${rule.value} ${rule.value === 1 ? 'kniha' : 'knih'} z ${rule.country}`;
            }

        case RULE_TYPES.BOOKS_BY_YEAR_RANGE:
            let rangeDesc;
            if (rule.yearFrom && rule.yearTo) {
                rangeDesc = `z let ${rule.yearFrom}-${rule.yearTo}`;
            } else if (rule.yearFrom) {
                rangeDesc = `od roku ${rule.yearFrom}`;
            } else if (rule.yearTo) {
                rangeDesc = `do roku ${rule.yearTo}`;
            } else {
                rangeDesc = `z jakéhokoliv roku`;
            }
            return `${operatorLabel} ${rule.value} ${rule.value === 1 ? 'kniha' : 'knih'} ${rangeDesc}`;

        case RULE_TYPES.TOTAL_COUNT:
            return `${operatorLabel} ${rule.value} knih celkem`;

        case RULE_TYPES.COMBINED:
            // Combined rules have pre-generated label
            return rule.combinedLabel || 'Kombinované pravidlo';

        default:
            return 'Neznámé pravidlo';
    }
};

/**
 * Validates a single rule
 * @param {Object} rule - Rule object
 * @param {Array} books - Array of book objects
 * @returns {{ruleId: string, ruleType: string, severity: string, result: Object}}
 */
export const validateRule = (rule, books) => {
    if (!rule) {
        return null;
    }

    let result;
    let severity;

    switch (rule.type) {
        case RULE_TYPES.COMBINED:
            // Validate all combined rules with AND logic
            const combinedResults = rule.combinedRules.map(r => validateRule(r, books));
            const allSatisfied = combinedResults.every(r => r && r.isSatisfied);
            const hasError = combinedResults.some(r => r && r.severity === 'error');
            const hasWarning = combinedResults.some(r => r && r.severity === 'warning');

            result = {
                isSatisfied: allSatisfied,
                current: null,
                required: null,
                message: allSatisfied
                    ? `Splněno: Všechna pravidla v kombinaci (${rule.combinedRules.length})`
                    : `Nesplněno: Některá pravidla v kombinaci nejsou splněna`,
                details: {
                    combinedResults
                }
            };
            severity = allSatisfied ? 'success' : (hasError ? 'error' : (hasWarning ? 'warning' : 'success'));
            break;

        case RULE_TYPES.BOOKS_PER_AUTHOR:
            result = validateBooksPerAuthor(rule, books);
            // MAX operator is blocking (error), MIN is warning
            severity = result.isSatisfied ? 'success' : (rule.operator === OPERATORS.MAX ? 'error' : 'warning');
            break;

        case RULE_TYPES.BOOKS_BY_TYPE:
            result = validateBooksByType(rule, books);
            // MAX operator is blocking (error), MIN is warning
            severity = result.isSatisfied ? 'success' : (rule.operator === OPERATORS.MAX ? 'error' : 'warning');
            break;

        case RULE_TYPES.BOOKS_BY_COUNTRY:
            result = validateBooksByCountry(rule, books);
            // MAX operator is blocking (error), MIN is warning
            severity = result.isSatisfied ? 'success' : (rule.operator === OPERATORS.MAX ? 'error' : 'warning');
            break;

        case RULE_TYPES.BOOKS_BY_YEAR_RANGE:
            result = validateBooksByYearRange(rule, books);
            // MAX operator is blocking (error), MIN is warning
            severity = result.isSatisfied ? 'success' : (rule.operator === OPERATORS.MAX ? 'error' : 'warning');
            break;

        case RULE_TYPES.TOTAL_COUNT:
            result = validateTotalCount(rule, books);
            severity = result.isSatisfied ? 'success' : (rule.operator === OPERATORS.MAX ? 'error' : 'warning');
            break;

        default:
            return null;
    }

    return {
        ruleId: rule.id,
        ruleType: rule.type,
        severity,
        ...result
    };
};

/**
 * Validates all rules and returns structured results
 * @param {Array} rules - Array of rule objects
 * @param {Array} books - Array of book objects
 * @returns {{isValid: boolean, violations: Array, warnings: Array, results: Object}}
 */
export const validateAllRules = (rules, books) => {
    const results = {};
    const violations = [];
    const warnings = [];

    // Validate all rules
    rules.forEach(rule => {
        const validation = validateRule(rule, books);
        if (validation) {
            results[rule.id] = validation;

            if (!validation.isSatisfied) {
                if (validation.severity === 'error') {
                    violations.push(validation);
                } else if (validation.severity === 'warning') {
                    warnings.push(validation);
                }
            }
        }
    });

    return {
        isValid: violations.length === 0,
        violations,
        warnings,
        results
    };
};

/**
 * Checks if adding a book would violate any MAX rules (blocking rules)
 * @param {Object} book - Book to be added
 * @param {Array} currentBooks - Current books in the list
 * @param {Array} rules - Array of rule objects
 * @returns {{canAdd: boolean, reason: string|null, violatedRule: Object|null}}
 */
export const canAddBook = (book, currentBooks, rules) => {
    // Flatten combined rules and check MAX rules
    const allRules = [];
    rules.forEach(rule => {
        if (rule.type === RULE_TYPES.COMBINED) {
            // Add all nested rules from combined rule
            rule.combinedRules.forEach(r => allRules.push(r));
        } else {
            allRules.push(rule);
        }
    });

    // Only check MAX rules (blocking rules)
    const maxRules = allRules.filter(rule => rule.operator === OPERATORS.MAX);

    for (const rule of maxRules) {
        // Simulate adding the book
        const simulatedBooks = [...currentBooks, book];

        // Validate the rule with the simulated book list
        const validation = validateRule(rule, simulatedBooks);

        if (validation && !validation.isSatisfied) {
            // Determine specific reason based on rule type
            let reason;

            switch (rule.type) {
                case RULE_TYPES.BOOKS_PER_AUTHOR:
                    reason = `Nelze přidat knihu: Již máte ${validation.current} knih od autora ${book.author?.name || 'tohoto autora'}. Maximum je ${rule.value}.`;
                    break;
                case RULE_TYPES.BOOKS_BY_TYPE:
                    reason = `Nelze přidat knihu: Již máte ${validation.current} knih literárního druhu ${rule.bookType}. Maximum je ${rule.value}.`;
                    break;
                case RULE_TYPES.BOOKS_BY_COUNTRY:
                    let litType;
                    if (rule.literatureType === LITERATURE_TYPES.CZECH) {
                        litType = 'české literatury';
                    } else if (rule.literatureType === LITERATURE_TYPES.WORLD) {
                        litType = 'světové literatury';
                    } else {
                        litType = `z ${rule.country}`;
                    }
                    reason = `Nelze přidat knihu: Již máte ${validation.current} knih ${litType}. Maximum je ${rule.value}.`;
                    break;
                case RULE_TYPES.BOOKS_BY_YEAR_RANGE:
                    let rangeDesc;
                    if (rule.yearFrom && rule.yearTo) {
                        rangeDesc = `z let ${rule.yearFrom}-${rule.yearTo}`;
                    } else if (rule.yearFrom) {
                        rangeDesc = `od roku ${rule.yearFrom}`;
                    } else if (rule.yearTo) {
                        rangeDesc = `do roku ${rule.yearTo}`;
                    } else {
                        rangeDesc = `z tohoto období`;
                    }
                    reason = `Nelze přidat knihu: Již máte ${validation.current} knih ${rangeDesc}. Maximum je ${rule.value}.`;
                    break;
                case RULE_TYPES.TOTAL_COUNT:
                    reason = `Nelze přidat knihu: Seznam již obsahuje ${validation.current} knih. Maximum je ${rule.value}.`;
                    break;

                default:
                    reason = `Nelze přidat knihu: Porušuje pravidlo maxima.`;
            }

            return {
                canAdd: false,
                reason,
                violatedRule: rule
            };
        }
    }

    return {
        canAdd: true,
        reason: null,
        violatedRule: null
    };
};

/**
 * Generates the official CERMAT maturita reading list rules (section 3.1).
 * Rules:
 *  - Světová a česká literatura do konce 18. stol.: min 2 díla
 *  - Světová a česká literatura 19. stol.: min 3 díla
 *  - Světová literatura 20. a 21. stol.: min 4 díla (COMBINED: světová + rok >= 1901)
 *  - Česká literatura 20. a 21. stol.: min 5 děl (COMBINED: česká + rok >= 1901)
 * @returns {Array} Array of rule objects
 */
export const generateMaturitaRules = () => {
    const now = Date.now();
    return [
        // Světová a česká do konce 18. stol. — min 2
        {
            id: generateRuleId(),
            type: RULE_TYPES.BOOKS_BY_YEAR_RANGE,
            operator: OPERATORS.MIN,
            value: 2,
            yearFrom: null,
            yearTo: 1800,
            createdAt: now
        },
        // Světová a česká 19. stol. — min 3
        {
            id: generateRuleId(),
            type: RULE_TYPES.BOOKS_BY_YEAR_RANGE,
            operator: OPERATORS.MIN,
            value: 3,
            yearFrom: 1801,
            yearTo: 1900,
            createdAt: now + 1
        },
        // Světová literatura 20. a 21. stol. — min 4
        {
            id: generateRuleId(),
            type: RULE_TYPES.COMBINED,
            combinedLabel: 'Min 4 světové literatury 20. a 21. stol.',
            combinedRules: [
                {
                    id: generateRuleId(),
                    type: RULE_TYPES.BOOKS_BY_COUNTRY,
                    literatureType: LITERATURE_TYPES.WORLD,
                    operator: OPERATORS.MIN,
                    value: 4
                },
                {
                    id: generateRuleId(),
                    type: RULE_TYPES.BOOKS_BY_YEAR_RANGE,
                    operator: OPERATORS.MIN,
                    value: 4,
                    yearFrom: 1901,
                    yearTo: null
                }
            ],
            createdAt: now + 2
        },
        // Česká literatura 20. a 21. stol. — min 5
        {
            id: generateRuleId(),
            type: RULE_TYPES.COMBINED,
            combinedLabel: 'Min 5 české literatury 20. a 21. stol.',
            combinedRules: [
                {
                    id: generateRuleId(),
                    type: RULE_TYPES.BOOKS_BY_COUNTRY,
                    literatureType: LITERATURE_TYPES.CZECH,
                    operator: OPERATORS.MIN,
                    value: 5
                },
                {
                    id: generateRuleId(),
                    type: RULE_TYPES.BOOKS_BY_YEAR_RANGE,
                    operator: OPERATORS.MIN,
                    value: 5,
                    yearFrom: 1901,
                    yearTo: null
                }
            ],
            createdAt: now + 3
        },
        // Min 2 prózy
        {
            id: generateRuleId(),
            type: RULE_TYPES.BOOKS_BY_TYPE,
            bookType: 'PROSE',
            operator: OPERATORS.MIN,
            value: 2,
            createdAt: now + 4
        },
        // Min 2 poezie
        {
            id: generateRuleId(),
            type: RULE_TYPES.BOOKS_BY_TYPE,
            bookType: 'POETRY',
            operator: OPERATORS.MIN,
            value: 2,
            createdAt: now + 5
        },
        // Min 2 dramata
        {
            id: generateRuleId(),
            type: RULE_TYPES.BOOKS_BY_TYPE,
            bookType: 'DRAMA',
            operator: OPERATORS.MIN,
            value: 2,
            createdAt: now + 6
        },
        // Max 2 díla od jednoho autora
        {
            id: generateRuleId(),
            type: RULE_TYPES.BOOKS_PER_AUTHOR,
            operator: OPERATORS.MAX,
            value: 2,
            createdAt: now + 7
        },
        // Celkem min 20 knih
        {
            id: generateRuleId(),
            type: RULE_TYPES.TOTAL_COUNT,
            operator: OPERATORS.MIN,
            value: 20,
            createdAt: now + 8
        },
        // Celkem max 20 knih
        {
            id: generateRuleId(),
            type: RULE_TYPES.TOTAL_COUNT,
            operator: OPERATORS.MAX,
            value: 20,
            createdAt: now + 9
        }
    ];
};

/**
 * Gets all current violations (errors and warnings)
 * @param {Array} rules - Array of rule objects
 * @param {Array} books - Array of book objects
 * @returns {{errors: Array, warnings: Array}}
 */
export const getViolations = (rules, books) => {
    const validation = validateAllRules(rules, books);
    return {
        errors: validation.violations,
        warnings: validation.warnings
    };
};
